import { HttpHeaders, HttpClient, HttpClientModule } from '@angular/common/http';
import { ɵɵdefineInjectable, Injectable, ɵɵinject, EventEmitter, Component, Output, Input, NgZone, NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { of, Observable, Subject } from 'rxjs';
import { map, share, catchError, tap, first } from 'rxjs/operators';
import { client, dataCollector, paypalCheckout } from 'braintree-web';
import { __awaiter } from 'tslib';
import { CommonModule } from '@angular/common';
import { GooglePayButtonModule } from '@google-pay/button-angular';

/**
 * Application Contants
 *
 * e.g.
 *
 * export const [variable-name] = [variable-value];
 */
/* eslint-disable */
// -----------------------------------------------------------------------------
// Payment
// -----------------------------------------------------------------------------
const COUNTRY_CODE = 'US';
const CURRENCY_CODE = 'USD';
const SHIP_STATES = ['MS', 'FL', 'AL', 'TN'];
// -----------------------------------------------------------------------------
// GooglePay
// -----------------------------------------------------------------------------
const GOOGLE_MERCHANT_INFO = {
    merchantName: 'C Spire',
    // Both merchant IDs are returning APPROVAL from Elavon
    merchantId: 'BCR2DN4TUS2634ZI',
};
const GOOGLE_METHODS_CARD = {
    type: 'CARD',
    parameters: {
        allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
        allowedCardNetworks: ['AMEX', 'DISCOVER', 'MASTERCARD', 'VISA'],
        billingAddressRequired: true,
        billingAddressParameters: { format: 'FULL' }
    },
    tokenizationSpecification: {
        type: 'PAYMENT_GATEWAY',
        parameters: {
            gateway: 'convergepay',
            // Both of these return APPROVAL from Elavon
            // 16 digit TID
            // gatewayMerchantId: '0008000404809270'
            // The full Terminal Number (6 digit BIN and 16 digit TID)
            gatewayMerchantId: '0017340008000404809270',
        }
    }
};
// -----------------------------------------------------------------------------
// ApplePay
// -----------------------------------------------------------------------------
const APPLE_SUPPORTED_NETWORKS = ['masterCard', 'visa', 'amex', 'discover'];
const APPLE_SUPPORTED_MINIMUM_VERSION = 13;
// -----------------------------------------------------------------------------
// PayPal
// -----------------------------------------------------------------------------
const GATEWAY_ID = 'wcp3swd2kkxm282d';
const MERCHANT_ACCOUNT_ORDERS_SANDBOX = 'CS_SANDBOX_ORDERS';
const MERCHANT_ACCOUNT_PAYMENTS_SANDBOX = 'CS_SANDBOX_PAYMENTS';
const MERCHANT_ACCOUNT_ORDERS_PRODUCTION = 'CS_PRODUCTION_ORDERS';
const MERCHANT_ACCOUNT_PAYMENTS_PRODUCTION = 'CS_PRODUCTION_PAYMENTS';
const MERCHANT_ACCOUNTS = {
    // CS_SANDBOX: GATEWAY_ID,
    CS_SANDBOX_ORDERS: 'orders',
    CS_SANDBOX_PAYMENTS: 'cspire',
    CS_PRODUCTION_ORDERS: 'orders',
    CS_PRODUCTION_PAYMENTS: 'cspire'
};
const CLIENT_INVALID_AUTHORIZATION = 'CLIENT_INVALID_AUTHORIZATION';
const CLIENT_AUTHORIZATION_INVALID = 'CLIENT_AUTHORIZATION_INVALID';
const PAYPAL_CLIENT_ERRORS = [CLIENT_INVALID_AUTHORIZATION, CLIENT_AUTHORIZATION_INVALID];
// -----------------------------------------------------------------------------
// PaymentService
// -----------------------------------------------------------------------------
const startApplePaySessionUrl = '/rest-api/account/payment/thirdparty/v1/startApplePaySession';
const submitApplePayPaymentUrl = '/rest-api/account/payment/thirdparty/v1/apply/applePay';
const submitGooglePayPaymentUrl = '/rest-api/account/payment/thirdparty/v1/apply/googlePay';
const submitPayPalPaymentUrl = '/rest-api/account/payment/thirdparty/v1/apply/paypal';
const getAllowedTenderTypesUrl = '/rest-api/account/payment/thirdparty/v1/getTenderTypes';
const startPayPalSessionUrl = '/rest-api/account/payment/thirdparty/v1/startPayPalSession';
const getPayPalServiceFeeUrl = '/rest-api/account/payment/thirdparty/v1/getTenderFee';
// -----------------------------------------------------------------------------
// Message Constants
// -----------------------------------------------------------------------------
const genericContentPaymentErrorMessage = 'We are unable to process your payment at this time. Please resubmit your payment using another payment method or try again later.';
// -----------------------------------------------------------------------------
// Enum
// -----------------------------------------------------------------------------
var ContentType;
(function (ContentType) {
    ContentType["urlFormEncoded"] = "application/x-www-form-urlencoded";
    ContentType["json"] = "application/json";
})(ContentType || (ContentType = {}));
var LOB;
(function (LOB) {
    LOB["WIRELESS"] = "WIRELESS";
    LOB["FIBER"] = "FIBER";
    LOB["BUSINESS"] = "BUSINESS";
})(LOB || (LOB = {}));
var ClientName;
(function (ClientName) {
    ClientName["MMA"] = "MMA";
    ClientName["ESTORE"] = "E-STORE";
    ClientName["MMAEXPRESS"] = "MMAExpress";
})(ClientName || (ClientName = {}));
var CardDataCaptureMethod;
(function (CardDataCaptureMethod) {
    CardDataCaptureMethod["MANUAL"] = "MANUAL";
})(CardDataCaptureMethod || (CardDataCaptureMethod = {}));
var TenderTypes;
(function (TenderTypes) {
    TenderTypes["PERSONAL_CHECK"] = "PERSONAL_CHECK";
    TenderTypes["ELECTRONIC_CHECK"] = "ELECTRONIC_CHECK";
    TenderTypes["GIFT_CARD"] = "GIFT_CARD";
    TenderTypes["BILL_TO_ACCOUNT"] = "BILL_TO_ACCOUNT";
    TenderTypes["CASH"] = "CASH";
    TenderTypes["DEBIT_CARD"] = "DEBIT_CARD";
    TenderTypes["CREDIT_CARD"] = "CREDIT_CARD";
    TenderTypes["TRAVELERS_CHECK"] = "TRAVELERS_CHECK";
    TenderTypes["MONEY_ORDER"] = "MONEY_ORDER";
    TenderTypes["COUPON"] = "COUPON";
    TenderTypes["PHYSICAL_CHECK"] = "PHYSICAL_CHECK";
    TenderTypes["INVOICE"] = "INVOICE";
    TenderTypes["GOOGLE_PAY"] = "GOOGLE_PAY";
    TenderTypes["APPLE_PAY"] = "APPLE_PAY";
    TenderTypes["PAYPAL"] = "PAYPAL";
})(TenderTypes || (TenderTypes = {}));

/**
 * AppState
 *
 * Object class that contains application state
 */
class AppState {
    constructor() {
        this.paypalStartSessionAttempts = 0;
    }
    /**
     * @param environment variable that contains the value of environment
     */
    setEnvironment(env) {
        this.environment = env;
    }
    /**
     * @returns the value of environment
     */
    getEnvironment() {
        if (this.environment) {
            return this.environment;
        }
        else {
            const newEnvironment = {
                production: this.getWindowHref().includes('/www.cspire.com') ? true : false,
                environmentName: this.getWindowHref().includes('/www.cspire.com') ? 'PROD' : 'DEV',
                apiUrl: this.getWindowHref(),
                microServiceUrl: this.getWindowHref(),
            };
            return newEnvironment;
        }
    }
    getWindowHref() {
        return window.location.origin;
    }
    getAllowedTenderTypesResponse() {
        return this.allowedTenderTypesResponse;
    }
    setAllowedTenderTypesResponse(allowedTenderTypesResponse) {
        this.allowedTenderTypesResponse = allowedTenderTypesResponse;
    }
    getAllowedAddLineTenderTypesResponse() {
        return this.allowedAddLineTenderTypesResponse;
    }
    setAllowedAddLineTenderTypesResponse(allowedAddLineTenderTypesResponse) {
        this.allowedAddLineTenderTypesResponse = allowedAddLineTenderTypesResponse;
    }
    getPayPalStartSessionAttempts() {
        return this.paypalStartSessionAttempts;
    }
    setPayPalStartSessionAttempts(paypalStartSessionAttempts) {
        this.paypalStartSessionAttempts = paypalStartSessionAttempts;
    }
}
AppState.ɵprov = ɵɵdefineInjectable({ factory: function AppState_Factory() { return new AppState(); }, token: AppState, providedIn: "root" });
AppState.decorators = [
    { type: Injectable, args: [{
                providedIn: 'root'
            },] }
];

class CsThirdPartyPaymentsService {
    constructor(appState, http) {
        this.appState = appState;
        this.http = http;
    }
    /**
     * @returns Gets allowed tender types
     */
    getAllowedTenderTypes(isAddLine) {
        if (isAddLine && this.appState.getAllowedAddLineTenderTypesResponse()) {
            return of(this.appState.getAllowedAddLineTenderTypesResponse());
        }
        if (!isAddLine && this.appState.getAllowedTenderTypesResponse()) {
            return of(this.appState.getAllowedTenderTypesResponse());
        }
        if (this.tenderTypesObservable) {
            return this.tenderTypesObservable;
        }
        let url = this.appState.getEnvironment().apiUrl + getAllowedTenderTypesUrl;
        if (isAddLine) {
            url += '?isAddLine=' + isAddLine;
        }
        this.tenderTypesObservable = this.http.post(url, null).pipe(map((res) => {
            if (isAddLine) {
                this.appState.setAllowedAddLineTenderTypesResponse(res);
            }
            else {
                this.appState.setAllowedTenderTypesResponse(res);
            }
            return res;
        }), share(), catchError((error) => {
            const obj = error.message;
            throw obj;
        }));
        return this.tenderTypesObservable;
    }
    /**
     * @returns Starts a new apple pay session
     */
    startApplePaySession() {
        return this.http.get(this.appState.getEnvironment().apiUrl + startApplePaySessionUrl).pipe(map((res) => res), catchError((error) => {
            const obj = error.message;
            throw obj;
        }));
    }
    /**
     * @returns Starts PayPal Session
     */
    startPayPalSession(clientName, requestFreshToken) {
        const headers = new HttpHeaders({ 'Content-Type': ContentType.json });
        const options = { headers };
        let url = this.appState.getEnvironment().apiUrl + startPayPalSessionUrl;
        url += '?requestFreshToken=' + requestFreshToken + '&clientName=' + clientName;
        return this.http.post(url, options).pipe(map((res) => res), catchError((error) => {
            throw error.message;
        }));
    }
    /**
     * @returns Submits ApplePay Payment
     */
    submitApplePayPayment(applePayPayment) {
        const headers = new HttpHeaders({ 'Content-Type': ContentType.json });
        const options = { headers };
        return this.http.post(this.appState.getEnvironment().apiUrl + submitApplePayPaymentUrl, applePayPayment, options).pipe(map((res) => res), catchError((error) => {
            throw error.message;
        }));
    }
    /**
     * @returns Submits GooglePay Payment
     */
    submitGooglePayPayment(googlePayPayment) {
        const headers = new HttpHeaders({ 'Content-Type': ContentType.json });
        const options = { headers };
        return this.http.post(this.appState.getEnvironment().apiUrl + submitGooglePayPaymentUrl, googlePayPayment, options).pipe(map((res) => res), catchError((error) => {
            throw error.message;
        }));
    }
    /**
     * @returns Submits PayPal Payment
     */
    submitPayPalPayment(paypalPaymentRequest) {
        const headers = new HttpHeaders({ 'Content-Type': ContentType.json });
        const options = { headers };
        return this.http.post(this.appState.getEnvironment().apiUrl + submitPayPalPaymentUrl, paypalPaymentRequest, options).pipe(map((res) => res), catchError((error) => {
            throw error.message;
        }));
    }
    getServiceFee(tenderFeeRequest) {
        return this.http.post(this.appState.getEnvironment().apiUrl + getPayPalServiceFeeUrl, tenderFeeRequest).pipe(map((data) => data), catchError((error) => {
            throw error.message;
        }));
    }
}
CsThirdPartyPaymentsService.ɵprov = ɵɵdefineInjectable({ factory: function CsThirdPartyPaymentsService_Factory() { return new CsThirdPartyPaymentsService(ɵɵinject(AppState), ɵɵinject(HttpClient)); }, token: CsThirdPartyPaymentsService, providedIn: "root" });
CsThirdPartyPaymentsService.decorators = [
    { type: Injectable, args: [{
                providedIn: 'root'
            },] }
];
CsThirdPartyPaymentsService.ctorParameters = () => [
    { type: AppState },
    { type: HttpClient }
];

class CsThirdPartyPaymentsUtilService {
    constructor(appState, csThirdPartyPaymentsService) {
        this.appState = appState;
        this.csThirdPartyPaymentsService = csThirdPartyPaymentsService;
    }
    getAvailableTenderTypes(isAddLine) {
        return new Observable((obs) => {
            this.csThirdPartyPaymentsService.getAllowedTenderTypes(isAddLine).subscribe((res) => {
                const tenders = res.tenderTypes;
                // Check if APPLE_PAY can be used by the browser
                if (!this.getCanUseApplePay() && tenders.includes(TenderTypes.APPLE_PAY)) {
                    const index = tenders.indexOf(TenderTypes.APPLE_PAY, 0);
                    if (index > -1) {
                        tenders.splice(index, 1);
                    }
                    console.log('Not Compatible Browser for: APPLE_PAY');
                }
                obs.next(tenders);
            }, () => {
                obs.next([]);
            });
        });
    }
    getCanUseApplePay() {
        var _a;
        if (((_a = window) === null || _a === void 0 ? void 0 : _a.ApplePaySession) && ApplePaySession.canMakePayments() && ApplePaySession.supportsVersion(APPLE_SUPPORTED_MINIMUM_VERSION)) {
            return true;
        }
        else {
            return false;
        }
    }
    ;
    checkIfPayPalSDKIsLoaded() {
        var _a;
        if ((_a = window) === null || _a === void 0 ? void 0 : _a.paypal) {
            return true;
        }
        else {
            return false;
        }
    }
    getPayPalDeviceInformation() {
        return this.paypalDeviceInformation;
    }
    splitName(name) {
        if (name) {
            const tempName = name.split(' ');
            let firstName = '';
            let lastName = '';
            if (tempName.length > 2) {
                lastName = tempName.slice(-1).join();
                firstName = tempName.slice(0, tempName.length - 1).join(' ');
            }
            else {
                firstName = tempName[0];
                lastName = tempName[tempName.length - 1];
            }
            const names = [];
            names.push(firstName, lastName);
            return names;
        }
        return ['', ''];
    }
    normalizePhoneNumber(number) {
        let val = number;
        if (val) {
            val = val.trim();
            val = val.replace('+1', '');
            val = val.replace(/-/g, '');
            val = val.replace(/\(/g, '');
            val = val.replace(/\)/g, '');
            val = val.trim();
            return val;
        }
        return '';
    }
    getZipCode(zipCodeValue) {
        let zipCodeRegExp = new RegExp(/[0-9]{5}/);
        let zipCode = zipCodeValue.match(zipCodeRegExp);
        return `${zipCode}`;
    }
    loadPayPalSDKForEstore(requestFreshToken) {
        let paypalSDKSubject = new Subject();
        // if value isn't provided, set it to false
        if (requestFreshToken === undefined || requestFreshToken === null) {
            requestFreshToken = false;
        }
        if (this.checkIfPayPalSDKIsLoaded()) {
            paypalSDKSubject.next(true);
        }
        else {
            this.getNewPayPalSession(ClientName.ESTORE, requestFreshToken).then((res) => {
                const merchantAccountId = res.merchantAccountId;
                this.createNewBraintreeClientInstance(ClientName.ESTORE, res.token).then((clientInstance) => {
                    // Get PayPal Device Data
                    this.getPayPalDeviceData(clientInstance);
                    // Get PayPal Checkout Instance - used to create buttons and load SDK
                    this.getPayPalCheckoutInstance(clientInstance, merchantAccountId).then((paypalCheckoutInstance) => {
                        // Check to see if paypalCheckoutInstance exists as well as if paypal exists on the window, if it doesn't, add it.
                        if (paypalCheckoutInstance && !this.checkIfPayPalSDKIsLoaded()) {
                            paypalCheckoutInstance.loadPayPalSDK({
                                components: 'buttons,messages',
                                currency: 'USD',
                                'enable-funding': 'paylater',
                                'disable-funding': 'card',
                                vault: false,
                                commit: false
                            }).then(() => {
                                paypalSDKSubject.next(true);
                            });
                        }
                        paypalSDKSubject.next(false);
                    }).catch(() => {
                        paypalSDKSubject.next(false);
                    });
                }).catch(() => {
                    paypalSDKSubject.next(false);
                });
            }).catch(() => {
                paypalSDKSubject.next(false);
            });
        }
        return paypalSDKSubject.asObservable();
    }
    createNewPayPalCheckoutInstance(clientName, requestFreshToken) {
        return new Observable((obs) => {
            this.getNewPayPalSession(clientName, requestFreshToken).then((res) => {
                const merchantAccountId = res.merchantAccountId;
                this.createNewBraintreeClientInstance(clientName, res.token).then((clientInstance) => {
                    // Get PayPal Device Data
                    this.getPayPalDeviceData(clientInstance);
                    // Get PayPal Checkout Instance - used to create buttons and load SDK
                    this.getPayPalCheckoutInstance(clientInstance, merchantAccountId).then((paypalCheckoutInstance) => {
                        return obs.next(paypalCheckoutInstance);
                    }).catch((err) => {
                        return obs.next(null);
                    });
                }).catch(() => {
                    return obs.next(null);
                });
            }).catch(() => {
                return obs.next(null);
            });
        });
    }
    getNewPayPalSession(clientName, requestFreshToken) {
        return new Promise((resolve, reject) => {
            this.csThirdPartyPaymentsService.startPayPalSession(clientName, requestFreshToken).subscribe((res) => {
                resolve(res);
            }, (err) => {
                reject(err);
            });
        });
    }
    createNewBraintreeClientInstance(clientName, authorizationToken) {
        return new Promise((resolve, reject) => {
            client.create({
                authorization: authorizationToken
            }).then((clientInstance) => {
                this.appState.setPayPalStartSessionAttempts(0);
                resolve(clientInstance);
            }).catch((err) => {
                // Stop if there was a problem creating the client.
                // This could happen if there is a network error or if the authorization
                // is invalid.
                if (err) {
                    // CLIENT_INVALID_AUTHORIZATION - token is expired or invalid completely
                    if (PAYPAL_CLIENT_ERRORS.includes(err.code) && this.appState.getPayPalStartSessionAttempts() < 3) {
                        console.warn('PayPal ClientToken returned CLIENT_INVALID_AUTHORIZATION, getting a new one');
                        return this.getNewPayPalSession(clientName, true).then((res) => {
                            this.appState.setPayPalStartSessionAttempts(this.appState.getPayPalStartSessionAttempts() + 1);
                            resolve(this.createNewBraintreeClientInstance(clientName, res.token));
                        });
                    }
                    else {
                        console.error('Error creating client:', err);
                        return reject(err);
                    }
                }
                else {
                    console.error('Error creating Braintree Client');
                    const newError = new Error();
                    newError.message = 'Error creating Braintree Client';
                    return reject(newError);
                }
            });
        });
    }
    getPayPalDeviceData(clientInstance) {
        dataCollector.create({
            client: clientInstance,
            paypal: true
        }, (err, dataCollectorInstance) => {
            if (err) {
                // Handle error
                console.error('Error creating client:', err);
            }
            // At this point, you should access the dataCollectorInstance.deviceData value and provide it
            // to your server, e.g. by injecting it into your form as a hidden input
            if (dataCollectorInstance === null || dataCollectorInstance === void 0 ? void 0 : dataCollectorInstance.deviceData) {
                const deviceData = JSON.parse(dataCollectorInstance.deviceData);
                this.paypalDeviceInformation = deviceData.correlation_id;
            }
        });
    }
    getPayPalCheckoutInstance(clientInstance, merchantId) {
        this.paypalCheckoutPromise = new Promise((resolve, reject) => {
            // Create a PayPal Checkout component.
            paypalCheckout.create({
                client: clientInstance,
                // merchantAccountId: MERCHANT_ACCOUNTS[MERCHANT_ACCOUNT_ORDERS_SANDBOX],
                merchantAccountId: merchantId,
            }, (paypalCheckoutErr, paypalCheckoutInstance) => {
                if (paypalCheckoutErr) {
                    console.error('Error creating client:', paypalCheckoutErr);
                    reject(paypalCheckoutErr);
                }
                if (paypalCheckoutInstance) {
                    this.paypalCheckoutInstance = paypalCheckoutInstance;
                    resolve(paypalCheckoutInstance);
                }
                else {
                    reject(paypalCheckoutInstance);
                }
            });
        });
        return this.paypalCheckoutPromise;
    }
}
CsThirdPartyPaymentsUtilService.ɵprov = ɵɵdefineInjectable({ factory: function CsThirdPartyPaymentsUtilService_Factory() { return new CsThirdPartyPaymentsUtilService(ɵɵinject(AppState), ɵɵinject(CsThirdPartyPaymentsService)); }, token: CsThirdPartyPaymentsUtilService, providedIn: "root" });
CsThirdPartyPaymentsUtilService.decorators = [
    { type: Injectable, args: [{
                providedIn: 'root'
            },] }
];
CsThirdPartyPaymentsUtilService.ctorParameters = () => [
    { type: AppState },
    { type: CsThirdPartyPaymentsService }
];

class CsThirdPartyPaymentsWebComponent {
    constructor(appState, utilService) {
        this.appState = appState;
        this.utilService = utilService;
        this.paymentResultEmitter = new EventEmitter();
        this.paymentShippingAddressEmitter = new EventEmitter();
        this.outputPaymentPayloadEmitter = new EventEmitter();
        this.showThirdPartyPaymentEmitter = new EventEmitter();
        this.requireShipping = false;
        this.outputPaymentDataOnly = false;
        this.isAutopayFlow = false;
        this.isAddLine = false;
        this.enableGooglePay = false;
        this.enableApplePay = false;
        this.enablePayPal = false;
    }
    ngOnInit() {
        this.showThirdPartyPaymentEmitter.emit(false);
        if (this.environment) {
            this.appState.setEnvironment(this.environment);
        }
        else {
            this.environment = this.appState.getEnvironment();
        }
        this.utilService.getAvailableTenderTypes(this.isAddLine).subscribe((res) => {
            this.enableGooglePay = res.includes(TenderTypes.GOOGLE_PAY);
            this.enableApplePay = res.includes(TenderTypes.APPLE_PAY);
            this.enablePayPal = res.includes(TenderTypes.PAYPAL);
            // Check if any payment options are disabled
            this.determineEnabledThirdPartyPaymentOptions();
            if (this.enableApplePay || this.enableGooglePay || this.enablePayPal) {
                this.showThirdPartyPaymentEmitter.emit(true);
            }
        }, () => {
            this.enableGooglePay = false;
            this.enableApplePay = false;
            this.enablePayPal = false;
        });
    }
    /**
     * @returns determine if any third party option is disabled
     */
    determineEnabledThirdPartyPaymentOptions() {
        if (this.enabledThirdPartyPaymentOptions != undefined) {
            this.enableGooglePay = this.enabledThirdPartyPaymentOptions.includes(TenderTypes.GOOGLE_PAY);
            this.enableApplePay = this.enabledThirdPartyPaymentOptions.includes(TenderTypes.APPLE_PAY);
            this.enablePayPal = this.enabledThirdPartyPaymentOptions.includes(TenderTypes.PAYPAL);
        }
    }
    handlePaymentResponse(response) {
        this.paymentResultEmitter.emit(response);
    }
    handleShippingResponse(shippingAddress) {
        this.paymentShippingAddressEmitter.emit(shippingAddress);
    }
    handlePayloadRequest(payloadOutput) {
        this.outputPaymentPayloadEmitter.emit(payloadOutput);
    }
}
CsThirdPartyPaymentsWebComponent.decorators = [
    { type: Component, args: [{
                selector: 'lib-cs-thirdpartypayments-web',
                template: "<div id=\"cs-thirdpartypayments-web\">\n    <lib-cs-google-pay *ngIf=\"enableGooglePay && !isAutopayFlow\"\n        [accountId]=\"accountId\"\n        [environment]=\"environment\"\n        [paymentAmount]=\"paymentDetails.paymentAmount\"\n        [adminFeeAmount]=\"paymentDetails.adminFeeAmount\"\n        [clientName]=\"clientName\"\n        [lob]=\"lob\"\n        [requireShipping]=\"requireShipping\"\n        [outputPaymentDataOnly]=\"outputPaymentDataOnly\"\n        (googleResponseEmitter)=\"handlePaymentResponse($event)\"\n        (googleShippingEmitter)=\"handleShippingResponse($event)\"\n        (googlePayloadEmitter)=\"handlePayloadRequest($event)\">\n    </lib-cs-google-pay>\n    <lib-cs-apple-pay *ngIf=\"enableApplePay && !isAutopayFlow\"\n        [accountId]=\"accountId\"\n        [environment]=\"environment\"\n        [paymentAmount]=\"paymentDetails.paymentAmount\"\n        [adminFeeAmount]=\"paymentDetails.adminFeeAmount\"\n        [clientName]=\"clientName\"\n        [lob]=\"lob\"\n        [requireShipping]=\"requireShipping\"\n        [outputPaymentDataOnly]=\"outputPaymentDataOnly\"\n        (appleResponseEmitter)=\"handlePaymentResponse($event)\"\n        (appleShippingEmitter)=\"handleShippingResponse($event)\"\n        (applePayloadEmitter)=\"handlePayloadRequest($event)\">\n    </lib-cs-apple-pay>\n    <lib-cs-paypal *ngIf=\"enablePayPal\"\n        [accountId]=\"accountId\"\n        [environment]=\"environment\"\n        [paymentAmount]=\"paymentDetails.paymentAmount\"\n        [adminFeeAmount]=\"paymentDetails.adminFeeAmount\"\n        [clientName]=\"clientName\"\n        [lob]=\"lob\"\n        [requireShipping]=\"requireShipping\"\n        [outputPaymentDataOnly]=\"outputPaymentDataOnly\"\n        [isAutopayFlow]=\"isAutopayFlow\"\n        (payPalResponseEmitter)=\"handlePaymentResponse($event)\"\n        (payPalShippingEmitter)=\"handleShippingResponse($event)\"\n        (payPalPayloadEmitter)=\"handlePayloadRequest($event)\">\n    </lib-cs-paypal>\n</div>",
                styles: ["#cs-thirdpartypayments-web{display:flex;flex-direction:column}"]
            },] }
];
CsThirdPartyPaymentsWebComponent.ctorParameters = () => [
    { type: AppState },
    { type: CsThirdPartyPaymentsUtilService }
];
CsThirdPartyPaymentsWebComponent.propDecorators = {
    paymentResultEmitter: [{ type: Output }],
    paymentShippingAddressEmitter: [{ type: Output }],
    outputPaymentPayloadEmitter: [{ type: Output }],
    showThirdPartyPaymentEmitter: [{ type: Output }],
    environment: [{ type: Input }],
    accountId: [{ type: Input }],
    paymentDetails: [{ type: Input }],
    clientName: [{ type: Input }],
    lob: [{ type: Input }],
    requireShipping: [{ type: Input }],
    outputPaymentDataOnly: [{ type: Input }],
    isAutopayFlow: [{ type: Input }],
    isAddLine: [{ type: Input }],
    enabledThirdPartyPaymentOptions: [{ type: Input }]
};

class CreditPaymentResponse {
}

class CsApplePayComponent {
    constructor(paymentService, ngZone, utilService) {
        this.paymentService = paymentService;
        this.ngZone = ngZone;
        this.utilService = utilService;
        this.appleResponseEmitter = new EventEmitter();
        this.appleShippingEmitter = new EventEmitter();
        this.applePayloadEmitter = new EventEmitter();
        this.canUseApplePay = false;
        this.isFeeFetched = false;
        this.disableApplePayButton = false;
        this.onApplePayClick = ($event) => {
            this.disableApplePayButton = true;
            const request = {
                countryCode: COUNTRY_CODE,
                currencyCode: CURRENCY_CODE,
                supportedNetworks: APPLE_SUPPORTED_NETWORKS,
                merchantCapabilities: ['supports3DS'],
                total: { label: 'Total', amount: this.totalDueWithServiceFee },
                requiredBillingContactFields: ['postalAddress', 'name', 'phone', 'email'],
                requiredShippingContactFields: this.requireShipping ? ['name', 'postalAddress', 'phone', 'email'] : undefined,
                lineItems: [
                    {
                        label: 'Subtotal',
                        amount: this.paymentAmount
                    },
                    {
                        label: 'Service Fee',
                        amount: this.appleFee
                    },
                ]
            };
            this.appleSession = new ApplePaySession(this.determineSupportedVersion(), request);
            this.appleSession.onvalidatemerchant = () => __awaiter(this, void 0, void 0, function* () {
                this.paymentService.startApplePaySession().subscribe((res) => {
                    this.appleMerchantSession = res;
                    this.disableApplePayButton = false;
                    try {
                        this.appleSession.completeMerchantValidation(this.appleMerchantSession);
                    }
                    catch (error) {
                        console.log(error);
                    }
                }, (error) => {
                    console.log(error);
                });
            });
            this.appleSession.onpaymentmethodselected = (event) => {
                const update = {
                    newTotal: {
                        label: 'Total',
                        amount: this.totalDueWithServiceFee
                    }
                };
                this.appleSession.completePaymentMethodSelection(update);
            };
            this.appleSession.onshippingcontactselected = (event) => {
                const newTotal = {
                    label: 'Total',
                    amount: this.totalDueWithServiceFee
                };
                let update = {
                    newTotal
                };
                if (!SHIP_STATES.includes(event.shippingContact.administrativeArea)) {
                    update.errors = [
                        new ApplePayError('addressUnserviceable', 'administrativeArea', 'Cannot ship outside of MS, AL, FL, TN')
                    ];
                }
                this.appleSession.completeShippingContactSelection(update);
            };
            this.appleSession.onpaymentauthorized = (paymentEvent) => {
                const submitRequest = {
                    clientName: this.clientName,
                    accountId: this.accountId,
                    lineOfBusiness: this.lob,
                    paymentAmount: +this.totalDueWithServiceFee,
                    convenienceFeeAmount: this.appleFee ? +this.appleFee : undefined,
                    adminFeeAmount: this.adminFeeAmount ? +this.adminFeeAmount : undefined,
                    cardDataCaptureMethod: CardDataCaptureMethod.MANUAL,
                    streetAddress: this.getBillingAddress(paymentEvent.payment.billingContact),
                    city: this.getBillingAddressCity(paymentEvent.payment.billingContact),
                    state: this.getBillingAddressState(paymentEvent.payment.billingContact),
                    zipCode: this.utilService.getZipCode(paymentEvent.payment.billingContact.postalCode),
                    lastFour: this.pullLastFour(paymentEvent.payment.token.paymentMethod.displayName),
                    applePay: JSON.stringify(paymentEvent.payment.token.paymentData)
                };
                this.applePayloadEmitter.emit(submitRequest);
                this.ngZone.run(() => {
                    if (!this.outputPaymentDataOnly) {
                        this.paymentService.submitApplePayPayment(submitRequest).subscribe((res) => {
                            this.appleSession.completePayment({ status: ApplePaySession.STATUS_SUCCESS });
                            const appleShippingContact = paymentEvent.payment.shippingContact;
                            if (this.requireShipping) {
                                const address = this.getShippingAddress(appleShippingContact);
                                this.appleShippingEmitter.emit(address);
                            }
                            this.appleResponseEmitter.emit(res);
                        }, (error) => {
                            const result = {
                                status: ApplePaySession.STATUS_FAILURE
                            };
                            this.appleSession.completePayment(result);
                        });
                    }
                    else {
                        this.appleSession.completePayment({ status: ApplePaySession.STATUS_SUCCESS });
                        if (this.requireShipping) {
                            const appleShippingContact = paymentEvent.payment.shippingContact;
                            const address = this.getShippingAddress(appleShippingContact);
                            this.appleShippingEmitter.emit(address);
                        }
                        this.appleResponseEmitter.emit(new CreditPaymentResponse());
                    }
                });
            };
            this.appleSession.oncancel = (event) => {
                // Payment cancelled by WebKit
                this.ngZone.run(() => {
                    this.disableApplePayButton = false;
                });
            };
            this.appleSession.begin();
        };
    }
    ngOnChanges() {
        this.isFeeFetched = false;
        // Currently tender fees aren't applicable for orders
        if (this.clientName !== ClientName.ESTORE) {
            this.getApplePayServiceFee();
        }
        else {
            // left blank
            this.totalDueWithServiceFee = this.paymentAmount;
            this.appleFee = '0';
            this.isFeeFetched = true;
        }
    }
    ngAfterViewInit() {
        this.getCanUseApplePay();
    }
    determineSupportedVersion() {
        if (ApplePaySession.supportsVersion(14)) {
            return 14;
        }
        return APPLE_SUPPORTED_MINIMUM_VERSION;
    }
    getBillingAddress(address) {
        const { addressLines } = address;
        return `${addressLines === null || addressLines === void 0 ? void 0 : addressLines.join(' ')}`;
    }
    getBillingAddressCity(address) {
        const { locality } = address;
        return `${locality}`;
    }
    getBillingAddressState(address) {
        const { administrativeArea } = address;
        return `${administrativeArea}`;
    }
    pullLastFour(cardDetails) {
        if (cardDetails) {
            return cardDetails.split(' ')[1];
        }
        else {
            return '';
        }
    }
    getShippingAddress(appleShippingContact) {
        var _a;
        const addressLines = appleShippingContact.addressLines;
        const address = {
            address1: (addressLines === null || addressLines === void 0 ? void 0 : addressLines.length) ? addressLines[0] : '',
            address2: (addressLines === null || addressLines === void 0 ? void 0 : addressLines.length) ? addressLines[1] : '',
            city: appleShippingContact.locality,
            state: appleShippingContact.administrativeArea,
            postalCode: appleShippingContact.postalCode,
            firstName: appleShippingContact.givenName,
            lastName: appleShippingContact.familyName,
            email: (_a = appleShippingContact.emailAddress) !== null && _a !== void 0 ? _a : '',
            phoneNumber: this.utilService.normalizePhoneNumber(appleShippingContact.phoneNumber)
        };
        return address;
    }
    getCanUseApplePay() {
        this.canUseApplePay = this.utilService.getCanUseApplePay();
    }
    ;
    getApplePayServiceFee() {
        const tenderFeeRequest = {
            tenderType: TenderTypes.APPLE_PAY,
            amount: this.paymentAmount,
            clientName: this.clientName,
            lineOfBusiness: this.lob
        };
        this.paymentService.getServiceFee(tenderFeeRequest).subscribe((res) => {
            this.totalDueWithServiceFee = res.totalAmountWithFee.toString();
            this.appleFee = res.feeAmount.toString();
            this.isFeeFetched = true;
        }, () => {
            // left blank
            this.totalDueWithServiceFee = this.paymentAmount;
            this.appleFee = '0';
            this.isFeeFetched = true;
        });
    }
}
CsApplePayComponent.decorators = [
    { type: Component, args: [{
                selector: 'lib-cs-apple-pay',
                template: "<div id=\"cs-apple-pay\" *ngIf=\"canUseApplePay && isFeeFetched\">\n    <button id=\"apple-btn\" class=\"apple-pay-button apple-pay-button-black\" [disabled]=\"disableApplePayButton\" (click)=\"onApplePayClick($event)\"></button>\n</div>\n",
                styles: ["#cs-apple-pay{padding:6px 12px;width:100%}#cs-apple-pay .apple-pay-button{width:250px;height:40px;display:inline-block;-webkit-appearance:-apple-pay-button;cursor:pointer;-apple-pay-button-type:pay}#cs-apple-pay .apple-pay-button-black{-apple-pay-button-style:#000}#cs-apple-pay .apple-pay-button-white{-apple-pay-button-style:#fff}#cs-apple-pay .apple-pay-button-white-outline{-apple-pay-button-style:white-outline}"]
            },] }
];
CsApplePayComponent.ctorParameters = () => [
    { type: CsThirdPartyPaymentsService },
    { type: NgZone },
    { type: CsThirdPartyPaymentsUtilService }
];
CsApplePayComponent.propDecorators = {
    appleResponseEmitter: [{ type: Output }],
    appleShippingEmitter: [{ type: Output }],
    applePayloadEmitter: [{ type: Output }],
    accountId: [{ type: Input }],
    paymentAmount: [{ type: Input }],
    adminFeeAmount: [{ type: Input }],
    clientName: [{ type: Input }],
    lob: [{ type: Input }],
    requireShipping: [{ type: Input }],
    outputPaymentDataOnly: [{ type: Input }]
};

class CsGooglePayComponent {
    constructor(appState, paymentService, utilService) {
        this.appState = appState;
        this.paymentService = paymentService;
        this.utilService = utilService;
        this.googleResponseEmitter = new EventEmitter();
        this.googleShippingEmitter = new EventEmitter();
        this.googlePayloadEmitter = new EventEmitter();
        this.isFeeFetched = false;
        this.onError = (event) => {
            console.error('error', event.error);
        };
        this.onPaymentAuthorized = (paymentData) => new Promise((resolve) => this.processPayment(paymentData)
            .then((result) => {
            if (this.requireShipping) {
                const shippingAddress = this.getShippingAddress(paymentData);
                this.googleShippingEmitter.emit(shippingAddress);
            }
            this.googleResponseEmitter.emit(result);
            resolve({ transactionState: 'SUCCESS' });
        })
            .catch((err) => {
            if (err === null || err === void 0 ? void 0 : err.message) {
                resolve({
                    transactionState: 'ERROR',
                    error: this.getOtherError(err.message)
                });
            }
            else {
                resolve({
                    transactionState: 'ERROR',
                    error: this.getOtherError('')
                });
            }
        }));
        this.onPaymentDataChanged = (intermediatePaymentData) => new Promise((resolve, reject) => {
            const shippingAddress = intermediatePaymentData.shippingAddress;
            const paymentDataRequestUpdate = {};
            if (intermediatePaymentData.callbackTrigger.includes('INITIALIZE') || intermediatePaymentData.callbackTrigger.includes('SHIPPING_ADDRESS')) {
                // Allows for testing with Google cards
                if (!this.environment.production) {
                    SHIP_STATES.push('CA');
                }
                if (!SHIP_STATES.includes(shippingAddress.administrativeArea)) {
                    paymentDataRequestUpdate.error = this.getUnserviceableAddressError();
                }
            }
            resolve(paymentDataRequestUpdate);
        });
    }
    ngOnInit() {
        if (this.environment) {
            this.appState.setEnvironment(this.environment);
        }
        else {
            this.environment = this.appState.getEnvironment();
        }
    }
    ngOnChanges() {
        // Currently tender fees aren't applicable for orders
        if (this.clientName !== ClientName.ESTORE) {
            this.getGooglePayServiceFee();
        }
        else {
            this.totalDueWithServiceFee = this.paymentAmount;
            this.googleFee = '0';
            this.buildGPayRequest();
        }
    }
    ;
    getBillingAddress(address) {
        const { address1, address2 } = address;
        if (address2) {
            return `${address1} ${address2}`;
        }
        else {
            return `${address1}`;
        }
    }
    getBillingAddressCity(address) {
        const { locality } = address;
        return `${locality}`;
    }
    getBillingAddressState(address) {
        const { administrativeArea } = address;
        return `${administrativeArea}`;
    }
    processPayment(paymentData) {
        var _a;
        paymentData.paymentMethodData.description = (_a = paymentData.paymentMethodData.description) === null || _a === void 0 ? void 0 : _a.replace('••••', '').replace(/[^\x00-\x7F]/g, "");
        const submitRequest = {
            clientName: this.clientName,
            accountId: this.accountId,
            lineOfBusiness: this.lob,
            paymentAmount: +this.totalDueWithServiceFee,
            convenienceFeeAmount: this.googleFee ? +this.googleFee : undefined,
            adminFeeAmount: this.adminFeeAmount ? +this.adminFeeAmount : undefined,
            cardDataCaptureMethod: CardDataCaptureMethod.MANUAL,
            streetAddress: this.getBillingAddress(paymentData.paymentMethodData.info.billingAddress),
            city: this.getBillingAddressCity(paymentData.paymentMethodData.info.billingAddress),
            state: this.getBillingAddressState(paymentData.paymentMethodData.info.billingAddress),
            zipCode: this.utilService.getZipCode(paymentData.paymentMethodData.info.billingAddress.postalCode),
            lastFour: paymentData.paymentMethodData.info.cardDetails,
            googlePay: JSON.stringify(paymentData)
        };
        this.googlePayloadEmitter.emit(submitRequest);
        if (!this.outputPaymentDataOnly) {
            return this.paymentService.submitGooglePayPayment(submitRequest).pipe(tap(data => data), first()).toPromise();
        }
        else {
            return Promise.resolve(new CreditPaymentResponse());
        }
    }
    getShippingAddress(paymentData) {
        var _a;
        const { shippingAddress, email } = paymentData;
        const address = {
            address1: shippingAddress.address1,
            address2: (_a = shippingAddress.address2) !== null && _a !== void 0 ? _a : '',
            city: shippingAddress.locality,
            state: shippingAddress.administrativeArea,
            postalCode: shippingAddress.postalCode,
            firstName: this.utilService.splitName(shippingAddress.name)[0],
            lastName: this.utilService.splitName(shippingAddress.name)[1],
            email: email !== null && email !== void 0 ? email : '',
            phoneNumber: this.utilService.normalizePhoneNumber(shippingAddress.phoneNumber)
        };
        return address;
    }
    getOtherError(message) {
        return {
            intent: 'PAYMENT_AUTHORIZATION',
            message: (message === null || message === void 0 ? void 0 : message.length) ? message : genericContentPaymentErrorMessage,
            reason: 'OTHER_ERROR'
        };
    }
    ;
    getUnserviceableAddressError() {
        return {
            reason: 'SHIPPING_ADDRESS_UNSERVICEABLE',
            message: 'Cannot ship outside of MS, AL, FL, TN',
            intent: 'SHIPPING_ADDRESS'
        };
    }
    ;
    getGooglePayServiceFee() {
        const tenderFeeRequest = {
            tenderType: TenderTypes.GOOGLE_PAY,
            amount: this.paymentAmount,
            clientName: this.clientName,
            lineOfBusiness: this.lob
        };
        this.paymentService.getServiceFee(tenderFeeRequest).subscribe((res) => {
            this.totalDueWithServiceFee = res.totalAmountWithFee.toString();
            this.googleFee = res.feeAmount.toString();
            this.buildGPayRequest();
        }, () => {
            // left blank
            this.totalDueWithServiceFee = this.paymentAmount;
            this.googleFee = '0';
            this.buildGPayRequest();
        });
    }
    buildGPayRequest() {
        this.isFeeFetched = true;
        this.googlePaymentRequest = {
            apiVersion: 2,
            apiVersionMinor: 0,
            allowedPaymentMethods: [GOOGLE_METHODS_CARD],
            merchantInfo: GOOGLE_MERCHANT_INFO,
            transactionInfo: {
                totalPriceStatus: 'FINAL',
                totalPriceLabel: 'Total',
                totalPrice: this.totalDueWithServiceFee,
                countryCode: 'US',
                currencyCode: 'USD',
                displayItems: [
                    {
                        label: 'Subtotal',
                        type: 'SUBTOTAL',
                        price: this.paymentAmount
                    },
                    {
                        label: 'Service Fee',
                        type: 'LINE_ITEM',
                        price: this.googleFee
                    }
                ]
            },
            emailRequired: true,
            shippingAddressParameters: {
                phoneNumberRequired: true,
                allowedCountryCodes: ['US']
            }
        };
    }
}
CsGooglePayComponent.decorators = [
    { type: Component, args: [{
                selector: 'lib-cs-google-pay',
                template: "<div id=\"cs-google-pay\" *ngIf=\"isFeeFetched\">\n    <google-pay-button\n        [environment]=\"environment.production ? 'PRODUCTION' : 'TEST'\"\n        [buttonType]=\"'plain'\"\n        [buttonSizeMode]=\"'fill'\"\n        [buttonColor]=\"'black'\"\n        [existingPaymentMethodRequired]=\"false\"\n        [paymentRequest]=\"{\n            apiVersion: googlePaymentRequest.apiVersion,\n            apiVersionMinor: googlePaymentRequest.apiVersionMinor,\n            allowedPaymentMethods: googlePaymentRequest.allowedPaymentMethods,\n            merchantInfo: googlePaymentRequest.merchantInfo,\n            transactionInfo: googlePaymentRequest.transactionInfo,\n            shippingAddressRequired: requireShipping,\n            shippingAddressParameters: googlePaymentRequest.shippingAddressParameters,\n            callbackIntents: requireShipping ? ['PAYMENT_AUTHORIZATION', 'SHIPPING_ADDRESS'] : ['PAYMENT_AUTHORIZATION'],\n            emailRequired: googlePaymentRequest.emailRequired\n        }\"\n        (error)=\"onError($event)\"\n        [paymentAuthorizedCallback]=\"onPaymentAuthorized\"\n        [paymentDataChangedCallback]=\"requireShipping ? onPaymentDataChanged : undefined\"\n        class=\"google-pay-button\"\n    ></google-pay-button>\n</div>",
                styles: ["#cs-google-pay{padding:6px 12px}#cs-google-pay .google-pay-button{width:100%}"]
            },] }
];
CsGooglePayComponent.ctorParameters = () => [
    { type: AppState },
    { type: CsThirdPartyPaymentsService },
    { type: CsThirdPartyPaymentsUtilService }
];
CsGooglePayComponent.propDecorators = {
    googleResponseEmitter: [{ type: Output }],
    googleShippingEmitter: [{ type: Output }],
    googlePayloadEmitter: [{ type: Output }],
    accountId: [{ type: Input }],
    environment: [{ type: Input }],
    paymentAmount: [{ type: Input }],
    adminFeeAmount: [{ type: Input }],
    clientName: [{ type: Input }],
    lob: [{ type: Input }],
    requireShipping: [{ type: Input }],
    outputPaymentDataOnly: [{ type: Input }]
};

class CsPayPalComponent {
    constructor(paymentService, utilService) {
        this.paymentService = paymentService;
        this.utilService = utilService;
        this.isError = false;
        this.isLoading = true;
        this.payPalResponseEmitter = new EventEmitter();
        this.payPalShippingEmitter = new EventEmitter();
        this.payPalPayloadEmitter = new EventEmitter();
    }
    ngOnChanges() {
        this.getPayPalServiceFee().then(() => {
            this.displayPayPal();
        });
    }
    displayPayPal() {
        try {
            this.utilService.createNewPayPalCheckoutInstance(this.clientName, false).subscribe((paypalCheckoutInstance) => {
                if (this.clientName === ClientName.ESTORE) {
                    // If estore flow, check to see if the SDK is already loaded, if it isn't, load it, otherwise just display the button
                    if (this.utilService.checkIfPayPalSDKIsLoaded()) {
                        this.displayPayPalOrderButton(paypalCheckoutInstance);
                    }
                    else {
                        paypalCheckoutInstance.loadPayPalSDK({
                            components: 'buttons,messages',
                            currency: 'USD',
                            'enable-funding': 'paylater',
                            'disable-funding': 'card',
                            vault: false,
                            commit: false
                        }).then(() => {
                            this.displayPayPalOrderButton(paypalCheckoutInstance);
                        }).catch(() => {
                            // intentionally left blank
                        });
                    }
                }
                else if (!this.isAutopayFlow) {
                    // Force reload of the SDK for Autopay and MMA flows
                    paypalCheckoutInstance.loadPayPalSDK({
                        components: 'buttons',
                        currency: 'USD',
                        'disable-funding': 'card,credit,paylater',
                        vault: true,
                        commit: true,
                        intent: 'authorize'
                    }).then(() => {
                        this.displaySinglePaymentButton(paypalCheckoutInstance);
                    }).catch(() => {
                        // intentionally left blank
                    });
                }
                else {
                    // Force reload of the SDK for Autopay and MMA flows
                    paypalCheckoutInstance.loadPayPalSDK({
                        components: 'buttons',
                        currency: 'USD',
                        'disable-funding': 'card,credit,paylater',
                        vault: true,
                        commit: true
                    }).then(() => {
                        this.displayAutopayButton(paypalCheckoutInstance);
                    }).catch(() => {
                        // intentionally left blank
                        this.isError = true;
                    });
                }
            }, () => {
                // Stop if there was a problem creating the client.
                // This could happen if there is a network error or if the authorization
                // is invalid.
            });
        }
        catch (err) {
            console.log(err);
            this.isError = true;
        }
    }
    displaySinglePaymentButton(paypalCheckoutInstance) {
        // Build Billing Agreement
        let billDescription = 'Pay your C Spire Bill: $' + this.totalDueWithServiceFee;
        if (this.payPalFee && +this.payPalFee > 0) {
            billDescription = 'Pay your C Spire Bill + service fee: $' + this.totalDueWithServiceFee;
        }
        const windowPayPal = window.paypal;
        windowPayPal.Buttons({
            style: {
                height: 40
            },
            createOrder: () => {
                return paypalCheckoutInstance.createPayment({
                    flow: 'checkout',
                    requestBillingAgreement: true,
                    enableShippingAddress: false,
                    amount: this.totalDueWithServiceFee,
                    currency: 'USD',
                    locale: 'en_US',
                    billingAgreementDetails: {
                        description: billDescription
                    },
                    lineItems: [
                        {
                            quantity: '1',
                            unitAmount: +this.totalDueWithServiceFee - +this.payPalFee,
                            name: 'Amount',
                            kind: 'debit'
                        },
                        {
                            quantity: '1',
                            unitAmount: this.payPalFee,
                            name: 'Service Fee',
                            kind: 'debit'
                        }
                    ]
                });
            },
            onApprove: (data, actions) => {
                return this.handlePayPalOnApprove(paypalCheckoutInstance, data, actions);
            },
            onCancel: () => {
                // handle case where user cancels
            },
            onError: (err) => {
                // handle case where error occurs
                console.log(err);
            }
        }).render('#paypal-single-payment-button');
    }
    displayAutopayButton(paypalCheckoutInstance) {
        this.isLoading = false;
        const windowPayPal = window.paypal;
        windowPayPal.Buttons({
            style: {
                height: 40
            },
            createBillingAgreement: () => {
                return paypalCheckoutInstance.createPayment({
                    flow: 'vault',
                    billingAgreementDescription: 'C Spire Autopay',
                });
            },
            onApprove: (data, actions) => {
                return this.handlePayPalOnApprove(paypalCheckoutInstance, data, actions);
            },
            onCancel: () => {
                // handle case where user cancels
            },
            onError: (err) => {
                // handle case where error occurs
                console.log(err);
                this.isError = true;
            }
        }).render('#paypal-autopay-button');
    }
    displayPayPalOrderButton(paypalCheckoutInstance) {
        if (paypalCheckoutInstance) {
            const paypal = window.paypal;
            paypal.Buttons({
                style: {
                    height: 40
                },
                createOrder: () => {
                    return paypalCheckoutInstance.createPayment({
                        flow: 'checkout',
                        enableShippingAddress: this.requireShipping,
                        shippingAddressEditable: this.requireShipping,
                        amount: this.totalDueWithServiceFee,
                        currency: 'USD',
                        locale: 'en_US',
                        lineItems: [
                            {
                                quantity: '1',
                                unitAmount: +this.totalDueWithServiceFee - +this.payPalFee,
                                name: 'Order Total',
                                kind: 'debit'
                            },
                            {
                                quantity: '1',
                                unitAmount: this.payPalFee,
                                name: 'Service Fee',
                                kind: 'debit'
                            }
                        ]
                    });
                },
                onApprove: (data, actions) => {
                    return this.handlePayPalOnApprove(paypalCheckoutInstance, data, actions);
                },
                onCancel: () => {
                    // handle case where user cancels
                },
                onError: (err) => {
                    // handle case where error occurs
                    console.log(err);
                }
            }).render('#paypal-order-button');
        }
    }
    handlePayPalOnApprove(paypalCheckoutInstance, data, actions) {
        // some logic here before tokenization happens below
        return paypalCheckoutInstance.tokenizePayment(data).then((payload) => {
            // If payload exists
            if (payload) {
                this.details = JSON.stringify(payload.details);
                this.nonce = payload.nonce;
                // Export Shipping Details
                if (payload.details && payload.details.shippingAddress && this.requireShipping) {
                    this.payPalShippingEmitter.emit(this.getShippingAddress(payload.details));
                }
                const submitRequest = {
                    clientName: this.clientName,
                    accountId: this.accountId,
                    lineOfBusiness: this.lob,
                    paymentAmount: +this.totalDueWithServiceFee,
                    adminFeeAmount: this.adminFeeAmount ? +this.adminFeeAmount : 0,
                    payPalFeeAmount: this.payPalFee ? +this.payPalFee : undefined,
                    cardDataCaptureMethod: CardDataCaptureMethod.MANUAL,
                    streetAddress: this.getBillingAddress(payload.details.billingAddress),
                    city: this.getBillingAddressCity(payload.details.billingAddress),
                    state: this.getBillingAddressState(payload.details.billingAddress),
                    zipCode: this.utilService.getZipCode(this.getBillingAddressZip(payload.details.billingAddress)),
                    lastFour: payload.type,
                    paypal: JSON.stringify(payload),
                    deviceData: this.utilService.getPayPalDeviceInformation(),
                    paypalOrderId: data.orderID
                };
                this.payPalPayloadEmitter.emit(submitRequest);
                // Single Payment or Autopay, skip processing payment
                if (!this.outputPaymentDataOnly && !this.isAutopayFlow) {
                    submitRequest.paymentMethodNonce = payload.nonce;
                    return new Promise((resolve, reject) => {
                        this.paymentService.submitPayPalPayment(submitRequest).subscribe((res) => {
                            this.payPalResponseEmitter.emit(res);
                            resolve();
                        }, (err) => {
                            this.generateError();
                            reject(err);
                        });
                    });
                }
                else {
                    return new Promise((resolve, reject) => {
                        resolve();
                    });
                }
            }
            else {
                this.generateError();
                return Promise.reject(new Error('There was an error processing your payment'));
            }
        }).catch((err) => {
            // Stop if there was a problem creating the client.
            // This could happen if there is a network error or if the authorization
            // is invalid.
            if (err) {
                console.error('Error tokenizing payment:', err);
                this.generateError();
            }
        });
    }
    generateError() {
        const errorResponse = new CreditPaymentResponse();
        const error = new Error();
        error.message = genericContentPaymentErrorMessage;
        error.name = "Payment";
        errorResponse.error = error;
        this.payPalResponseEmitter.emit(errorResponse);
    }
    getBillingAddress(address) {
        if (address) {
            const { line1, line2 } = address;
            if (line2) {
                return `${line1} ${line2}`;
            }
            else {
                return `${line1}`;
            }
        }
        return '';
    }
    getBillingAddressCity(address) {
        if (address) {
            const { city } = address;
            return `${city}`;
        }
        return '';
    }
    getBillingAddressState(address) {
        if (address) {
            const { state } = address;
            return `${state}`;
        }
        return '';
    }
    getBillingAddressZip(address) {
        if (address) {
            const { postalCode } = address;
            return `${postalCode}`;
        }
        return '';
    }
    getShippingAddress(payload) {
        var _a, _b;
        const address = {
            address1: payload.shippingAddress.line1,
            address2: (_a = payload.shippingAddress.line2) !== null && _a !== void 0 ? _a : '',
            city: payload.shippingAddress.city,
            state: payload.shippingAddress.state,
            postalCode: payload.shippingAddress.postalCode,
            firstName: this.utilService.splitName(payload.shippingAddress.recipientName)[0],
            lastName: this.utilService.splitName(payload.shippingAddress.recipientName)[1],
            email: (_b = payload.email) !== null && _b !== void 0 ? _b : '',
            phoneNumber: this.utilService.normalizePhoneNumber(payload.phone)
        };
        return address;
    }
    getPayPalServiceFee() {
        const tenderFeeRequest = {
            tenderType: TenderTypes.PAYPAL,
            amount: this.paymentAmount,
            clientName: this.clientName,
            lineOfBusiness: this.lob
        };
        return new Promise((resolve, reject) => {
            this.paymentService.getServiceFee(tenderFeeRequest).subscribe((res) => {
                this.totalDueWithServiceFee = res.totalAmountWithFee.toString();
                this.payPalFee = res.feeAmount.toString();
                resolve();
            }, () => {
                // left blank
                this.totalDueWithServiceFee = this.paymentAmount;
                this.payPalFee = '0';
                resolve();
            });
        });
    }
}
CsPayPalComponent.decorators = [
    { type: Component, args: [{
                selector: 'lib-cs-paypal',
                template: "<div id=\"cs-paypal\">\n    <!-- PayPal Button -->\n    <div id=\"paypal-order-button\"></div>\n    <!-- PayPal Button -->\n    <div id=\"paypal-single-payment-button\"></div>\n    <!-- PayPal Button -->\n    <div id=\"paypal-autopay-button\">\n        <img src=\"/resources/images/loading-transparent.gif\" alt=\"Loading\" *ngIf=\"!isError && isAutopayFlow && isLoading\">\n        <div class=\"error\" *ngIf=\"isError\">There was an error loading PayPal. Please try again.</div>\n    </div>\n</div>\n",
                styles: ["#cs-paypal{padding:6px 12px}#cs-paypal .error{color:red}"]
            },] }
];
CsPayPalComponent.ctorParameters = () => [
    { type: CsThirdPartyPaymentsService },
    { type: CsThirdPartyPaymentsUtilService }
];
CsPayPalComponent.propDecorators = {
    payPalResponseEmitter: [{ type: Output }],
    payPalShippingEmitter: [{ type: Output }],
    payPalPayloadEmitter: [{ type: Output }],
    accountId: [{ type: Input }],
    environment: [{ type: Input }],
    paymentAmount: [{ type: Input }],
    adminFeeAmount: [{ type: Input }],
    clientName: [{ type: Input }],
    lob: [{ type: Input }],
    requireShipping: [{ type: Input }],
    outputPaymentDataOnly: [{ type: Input }],
    isAutopayFlow: [{ type: Input }]
};

class CsPayPalPayLaterComponent {
    constructor(utilService) {
        this.utilService = utilService;
    }
    ngOnInit() {
    }
    ngOnChanges() {
        this.utilService.getAvailableTenderTypes(false).subscribe((res) => {
            if (res.includes(TenderTypes.PAYPAL)) {
                this.enablePayPalPayLater = true;
            }
        }, () => { });
    }
}
CsPayPalPayLaterComponent.decorators = [
    { type: Component, args: [{
                selector: 'lib-cs-paypal-pay-later',
                template: "<div data-pp-message [attr.data-pp-amount]=\"paypalPayLaterAmount\" *ngIf=\"enablePayPalPayLater\"></div>",
                styles: [""]
            },] }
];
CsPayPalPayLaterComponent.ctorParameters = () => [
    { type: CsThirdPartyPaymentsUtilService }
];
CsPayPalPayLaterComponent.propDecorators = {
    paypalPayLaterAmount: [{ type: Input }]
};

class CsApplePayModule {
}
CsApplePayModule.decorators = [
    { type: NgModule, args: [{
                imports: [
                    CommonModule
                ],
                providers: [
                    CsThirdPartyPaymentsService,
                    CsThirdPartyPaymentsUtilService
                ],
                declarations: [
                    CsApplePayComponent
                ],
                exports: [
                    CsApplePayComponent
                ],
                schemas: [CUSTOM_ELEMENTS_SCHEMA]
            },] }
];

class CsGooglePayModule {
}
CsGooglePayModule.decorators = [
    { type: NgModule, args: [{
                imports: [
                    CommonModule,
                    GooglePayButtonModule
                ],
                providers: [
                    CsThirdPartyPaymentsService
                ],
                declarations: [
                    CsGooglePayComponent
                ],
                exports: [
                    CsGooglePayComponent
                ],
                schemas: [CUSTOM_ELEMENTS_SCHEMA]
            },] }
];

class CsPayPalModule {
}
CsPayPalModule.decorators = [
    { type: NgModule, args: [{
                imports: [
                    CommonModule
                ],
                providers: [
                    CsThirdPartyPaymentsService
                ],
                declarations: [
                    CsPayPalComponent
                ],
                exports: [
                    CsPayPalComponent
                ],
                schemas: [CUSTOM_ELEMENTS_SCHEMA]
            },] }
];

class CsPayPalPayLaterModule {
}
CsPayPalPayLaterModule.decorators = [
    { type: NgModule, args: [{
                imports: [
                    CommonModule
                ],
                providers: [
                    CsThirdPartyPaymentsService,
                    CsThirdPartyPaymentsUtilService
                ],
                declarations: [
                    CsPayPalPayLaterComponent
                ],
                exports: [
                    CsPayPalPayLaterComponent
                ],
                schemas: [CUSTOM_ELEMENTS_SCHEMA]
            },] }
];

class CsThirdPartyPaymentsWebModule {
}
CsThirdPartyPaymentsWebModule.decorators = [
    { type: NgModule, args: [{
                imports: [
                    CommonModule,
                    HttpClientModule,
                    CsApplePayModule,
                    CsGooglePayModule,
                    CsPayPalModule,
                    CsPayPalPayLaterModule
                ],
                providers: [
                    CsThirdPartyPaymentsService,
                    AppState
                ],
                declarations: [
                    CsThirdPartyPaymentsWebComponent
                ],
                exports: [
                    CsThirdPartyPaymentsWebComponent
                ],
                schemas: [CUSTOM_ELEMENTS_SCHEMA]
            },] }
];

class PaymentDetails {
}

class ShippingAddress {
}

class SubmitGooglePayPaymentRequest {
}

class SubmitApplePayPaymentRequest {
}

/*
 * Public API Surface of cs-thirdpartypayments-web
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ClientName, CreditPaymentResponse, CsApplePayComponent, CsApplePayModule, CsGooglePayComponent, CsGooglePayModule, CsPayPalComponent, CsPayPalModule, CsPayPalPayLaterComponent, CsPayPalPayLaterModule, CsThirdPartyPaymentsService, CsThirdPartyPaymentsUtilService, CsThirdPartyPaymentsWebComponent, CsThirdPartyPaymentsWebModule, LOB, PaymentDetails, ShippingAddress, SubmitApplePayPaymentRequest, SubmitGooglePayPaymentRequest, TenderTypes, AppState as ɵa };
//# sourceMappingURL=cspire-cs-thirdpartypayments-web.js.map
